import React from "react";
import { cilInfo } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import { CBadge, CCard, CCardBody, CCardHeader, CCol, CRow, CTooltip } from "@coreui/react";
import type * as TDT from "@tradingdroid/frontends-types";

import { ColoredValue } from "@src/components/ColoredValue";

interface Props {
  robustness?: TDT.ApiStudyRobustness;
}

const PBO_OVERFIT_THRESHOLD = 0.5;

const HELP = {
  pbo: "Probability of Backtest Overfitting. Splits the period into chunks and, across every train/test combination, counts how often the trial that won in-sample lands below average out-of-sample. Over 50% means picking the winner of this study is worse than picking at random.",
  dsr: "Deflated Sharpe Ratio. The more trials you run, the more likely a great Sharpe is luck. This discounts the best Sharpe for how many trials it was picked from, and reports the chance the edge is real. Near 100% is good, near 0% means the winner looks no better than noise.",
  plateau:
    "Whether the winner sits on a broad flat region of the parameter space or on a lone spike. A neighborhood that also performs well survives live trading; an isolated spike usually does not.",
  pboValue: "Share of splits where the in-sample winner ranked below the out-of-sample median.",
  dsrValue: "Probability the true Sharpe beats the benchmark, after deflating for trial count.",
  srBest: "Best Sharpe found, per period (not annualised).",
  sr0: "Sharpe you would expect from the best of this many random trials. Best SR must beat this.",
  medianOos:
    "Typical out-of-sample minus in-sample gap. Negative means results decay out-of-sample.",
  splits: "Number of train/test combinations tested.",
  uniquePoints: "Distinct parameter sets tried, after collapsing duplicates.",
  scoredCandidates: "Top points by objective that got a plateau score.",
  objectiveRange: "Spread between the best and worst objective, used to scale the plateau score.",
  applicability:
    "Full when every trial optimised the same parameters. Partial when conditional parameters make some trials non-comparable.",
  rankedTrials: "Trials with the most stable neighborhood, best first.",
  bestSharpeTrial: "The trial the DSR was computed on.",
  uniqueTrials: "Distinct parameter sets feeding the DSR.",
} as const;

const STATUS_LABELS: Record<string, string> = {
  unavailable: "Not available",
  unavailable_needs_rerun: "Needs re-run (older study, no segment stats)",
  unavailable_unsupported_objective: "Objective not supported",
  low_power_short_segments: "Low power (segments too short)",
  low_power: "Low power",
  error: "Failed to compute",
  degenerate_objective: "Degenerate objective",
  no_valid_candidates: "No valid candidates",
  no_trials: "No trials",
};

const formatStatus = (status?: string): string =>
  status ? (STATUS_LABELS[status] ?? status.replace(/_/g, " ")) : "Unknown";

const InfoHint: React.FC<{ help: string }> = ({ help }) => (
  <CTooltip content={help} placement="top">
    <CIcon className="ms-1 text-info" icon={cilInfo} size="sm" style={{ cursor: "help" }} />
  </CTooltip>
);

const Metric: React.FC<{ label: string; help?: string; children: React.ReactNode }> = ({
  label,
  help,
  children,
}) => (
  <div className="StudyRobustnessMetric d-flex justify-content-between mb-1">
    <strong>
      {label}: {help && <InfoHint help={help} />}
    </strong>
    {children}
  </div>
);

const Subtitle: React.FC<{ title: string; help: string }> = ({ title, help }) => (
  <h6 className="StudyRobustnessSubtitle">
    {title} <InfoHint help={help} />
  </h6>
);

const Unavailable: React.FC<{ status?: string }> = ({ status }) => (
  <p className="StudyRobustnessUnavailable text-body-secondary mb-0">{formatStatus(status)}</p>
);

const Warnings: React.FC<{ warnings?: string[] }> = ({ warnings }) =>
  warnings?.length ? (
    <p className="StudyRobustnessWarnings text-body-secondary fs-sm mt-2 mb-0">
      {warnings.map((w) => w.replace(/_/g, " ")).join(", ")}
    </p>
  ) : null;

const PboBlock: React.FC<{ pbo?: TDT.ApiStudyPbo }> = ({ pbo }) => {
  if (!pbo || pbo.status !== "ok" || pbo.pbo === undefined) {
    return <Unavailable status={pbo?.status} />;
  }

  const isOverfit = pbo.pbo > PBO_OVERFIT_THRESHOLD;

  return (
    <>
      <Metric label="PBO" help={HELP.pboValue}>
        <CBadge color={isOverfit ? "danger" : "success"}>{(pbo.pbo * 100).toFixed(1)}%</CBadge>
      </Metric>
      <Metric label="Verdict">
        <span className={isOverfit ? "text-loss" : "text-win"}>
          {isOverfit ? "Selection likely overfit" : "Selection not likely overfit"}
        </span>
      </Metric>
      {pbo.metric && (
        <Metric label="Metric">
          <span>{pbo.metric.replace(/_/g, " ")}</span>
        </Metric>
      )}
      {pbo.splits?.n_splits !== undefined && (
        <Metric label="Splits" help={HELP.splits}>
          <ColoredValue value={pbo.splits.n_splits} type="flat" />
        </Metric>
      )}
      {pbo.degradation?.median_oos_minus_is !== undefined && (
        <Metric label="Median OOS − IS" help={HELP.medianOos}>
          <ColoredValue value={pbo.degradation.median_oos_minus_is} threshold={0} />
        </Metric>
      )}
      <Warnings warnings={pbo.warnings} />
    </>
  );
};

const DsrBlock: React.FC<{ dsr?: TDT.ApiStudyDsr }> = ({ dsr }) => {
  if (!dsr || dsr.status === "unavailable" || dsr.dsr === undefined) {
    return <Unavailable status={dsr?.status} />;
  }

  return (
    <>
      <Metric label="DSR" help={HELP.dsrValue}>
        <span className="d-flex gap-1">
          {dsr.status === "low_power" && <CBadge color="secondary">low power</CBadge>}
          <CBadge color={dsr.dsr >= 0.95 ? "success" : dsr.dsr >= 0.5 ? "warning" : "danger"}>
            {(dsr.dsr * 100).toFixed(1)}%
          </CBadge>
        </span>
      </Metric>
      {dsr.sr_best !== undefined && (
        <Metric label="Best SR (per period)" help={HELP.srBest}>
          <ColoredValue value={dsr.sr_best} threshold={0} decimals={4} />
        </Metric>
      )}
      {dsr.sr0 !== undefined && (
        <Metric label="Benchmark SR₀" help={HELP.sr0}>
          <ColoredValue value={dsr.sr0} type="flat" decimals={4} />
        </Metric>
      )}
      {dsr.best_sharpe_trial_number !== undefined && (
        <Metric label="Best Sharpe trial" help={HELP.bestSharpeTrial}>
          <span>#{dsr.best_sharpe_trial_number}</span>
        </Metric>
      )}
      {dsr.n_unique !== undefined && (
        <Metric label="Unique trials" help={HELP.uniqueTrials}>
          <ColoredValue value={dsr.n_unique} type="flat" />
        </Metric>
      )}
      <Warnings warnings={dsr.warnings} />
    </>
  );
};

const PlateauBlock: React.FC<{ plateau?: TDT.ApiStudyPlateau }> = ({ plateau }) => {
  if (!plateau || plateau.status !== "ok") {
    return <Unavailable status={plateau?.status} />;
  }

  return (
    <>
      {plateau.unique_count !== undefined && (
        <Metric label="Unique points" help={HELP.uniquePoints}>
          <ColoredValue value={plateau.unique_count} type="flat" />
        </Metric>
      )}
      {plateau.candidate_count !== undefined && (
        <Metric label="Scored candidates" help={HELP.scoredCandidates}>
          <ColoredValue value={plateau.candidate_count} type="flat" />
        </Metric>
      )}
      {plateau.objective_range !== undefined && (
        <Metric label="Objective range" help={HELP.objectiveRange}>
          <ColoredValue value={plateau.objective_range} type="flat" />
        </Metric>
      )}
      {plateau.applicability_quality && (
        <Metric label="Applicability" help={HELP.applicability}>
          <CBadge color={plateau.applicability_quality === "full" ? "success" : "warning"}>
            {plateau.applicability_quality}
          </CBadge>
        </Metric>
      )}
      {plateau.ranked_trials?.length ? (
        <Metric label="Top plateau trials" help={HELP.rankedTrials}>
          <span>
            {plateau.ranked_trials
              .slice(0, 5)
              .map((n) => `#${n}`)
              .join(", ")}
          </span>
        </Metric>
      ) : null}
      <Warnings warnings={plateau.warnings} />
    </>
  );
};

/**
 * Shows the study's overfit-resistance signals: PBO, deflated Sharpe and plateau stability.
 */
export const StudyRobustness: React.FC<Props> = ({ robustness }) => {
  if (!robustness) return null;

  if (robustness.status !== "ok") {
    return (
      <CCard className="StudyRobustness mb-4">
        <CCardHeader className="StudyRobustnessHeader">
          <h6 className="mb-0">Robustness</h6>
        </CCardHeader>
        <CCardBody>
          <Unavailable status={robustness.status} />
          <Warnings warnings={robustness.warnings} />
        </CCardBody>
      </CCard>
    );
  }

  return (
    <CCard className="StudyRobustness mb-4">
      <CCardHeader className="StudyRobustnessHeader">
        <h6 className="mb-0">
          Robustness{" "}
          <span className="text-body-secondary fw-normal fs-sm">
            Advisory evidence from the sampled surface, not out-of-sample proof
          </span>
        </h6>
      </CCardHeader>
      <CCardBody className="StudyRobustnessBody">
        <CRow>
          <CCol md={4} className="StudyRobustnessPboCol">
            <Subtitle title="Selection overfit (PBO)" help={HELP.pbo} />
            <PboBlock pbo={robustness.pbo} />
          </CCol>
          <CCol md={4} className="StudyRobustnessDsrCol">
            <Subtitle title="Deflated Sharpe (DSR)" help={HELP.dsr} />
            <DsrBlock dsr={robustness.dsr} />
          </CCol>
          <CCol md={4} className="StudyRobustnessPlateauCol">
            <Subtitle title="Plateau stability" help={HELP.plateau} />
            <PlateauBlock plateau={robustness.plateau} />
          </CCol>
        </CRow>
        <Warnings warnings={robustness.warnings} />
      </CCardBody>
    </CCard>
  );
};
